import { ReactNode } from 'react';
import { ITradeIn } from '../TradeInContext/TradeInContext.types';
import { DefaultProduct, IProduct } from '../../types/Product';
import { ILineItemPromoResponse, IPromoSummary } from './Promo/Promo.types';
import { IPlanSelection } from '../../pages/Plans/PlanPage.types';
import { DefaultShippingCost } from 'types/Shipping';
import { v4 as uuidv4 } from 'uuid';
import { IProductSku } from '../../types/ProductSku';
import { ICreateCartResponse, IReservePhoneNumbersResponse } from '../../api/Idi/Idi.api.types';
import { IAssurance } from '../../types/Assurance';
import { ISku } from '../../types/Sku';

export enum ShippingMethod {
    StorePickup,
    PostOffice
}

export interface ICartProps {
    customerInfo: ICustomerInfo;
    alternateContact: IAlternateContact;
    isDuplicateCustomer: boolean;
    autopay: boolean;
    eBill: boolean;
    agreements: ICustomerAgreements;
    lineItems: ILineItem[];
    accessories: IAccessoryItem[];
    shippingMethod: IShippingMethod;
    paymentConfirmationId: string;
    monthlySummary: ICostSummaryItem[];
    dueTodaySummary: ICostSummaryItem[];
    fees: ICostSummaryItem[];
    reserveNumberResponse: IReservePhoneNumbersResponse;
    idiCart?: ICreateCartResponse;
    closestStoreTimeZone?: string;
}

export interface ICartContext {
    cart: ICartProps;
    setCart: (newProps: ICartProps) => void;
    cartStepIndex: number;
    setCartStepIndex: (value: number) => void;
    reloadSession: () => void;
    isCartLoaded: boolean;
}

export interface ICartContextProvider {
    children: ReactNode;
}

export interface IUseCartHook {
    cart: ICartProps;
    cartStepIndex: number;
    setCartStepIndex: (value: number) => void;
    confirmOrder: () => string;
    confirmInternetOrder: () => void;
    reloadSession: () => void;
    setCart: (newProps: ICartProps) => void;
    handleUpdateCart: (updatedCart: ICartProps) => void;
    updateCartStepIndex: (updatedIndex: number) => void;
    updateCustomerInfo: (customerInfo: ICustomerInfo) => void;
    updateAlternateContact: (alternateContact: IAlternateContact) => void;
    updateAgreements: (agreements: ICustomerAgreements) => void;
    updateAccessories: (accessories: IAccessoryItem[]) => void;
    updateLineItems: (phones: ILineItem[]) => void;
    updateShippingMethod: (method: IShippingMethod) => void;
    updateAutopay: (isAutopay: boolean) => void;
    updateEbill: (isEbill: boolean) => void;
    updateReservedNumbers: (reservedNumbers: IReservePhoneNumbersResponse) => void;
    SyncLineItemData: (updatedCart: ICartProps) => void;
    setIdiCart: (idiCart: ICreateCartResponse) => void;
    setIsDuplicateCustomer: (isDuplicate: boolean) => void;
    clearCart: () => void;
    isCartLoaded: boolean;
}

export interface ILineItem {
    lineNumber: number;
    isPrimaryLine: boolean;
    phoneNumberInformation: IPhoneNumberInformation;
    promoResponses?: ILineItemPromoResponse[];
    byod?: IByod;
    device?: IDeviceItem;
    planSelection?: IPlanSelection;
    assurance?: IAssurance;
    promoSummary?: IPromoSummary;
}

export interface IPhoneNumberInformation {
    acceptedPhoneNumberAgreement?: boolean;
    needsNewNumber: boolean;
    oldNumber?: string;
}

export const DefaultLineItem = (): ILineItem => {
    return {
        lineNumber: 1,
        phoneNumberInformation: {
            needsNewNumber: true
        },
        device: DefaultDeviceItem(),
        promoResponses: [],
        isPrimaryLine: false
    };
};

export interface IByod {
    make: string;
    model: string;
    imei: string;
    file: File;
    fileName?: string;
}

export interface ICostSummaryItem {
    amount: number;
    itemName: string;
    /**
     * Line number. Used to determine the order in which items are displayed.
     */
    lineNumber: number;
    /**
     * The sku of the associated item. Optional.
     */
    sku?: string;
    /**
     * Type of CostSummaryItem. 'display' is intended for display purposes only and should not be
     * included as part of any calculation.
     */
    type: 'cost' | 'discount' | 'display';
    children?: ICostSummaryItem[];
}

export const DefaultCostSummaryItem = (): ICostSummaryItem => {
    return {
        amount: 0,
        itemName: '',
        lineNumber: 0,
        sku: '',
        type: 'cost'
    };
};

export interface IAccessoryItem extends ISharedProductItem {
    quantity: number;
    cartValue: number;
}

export interface ISharedProductItem {
    product: IProduct;
    image: string;
    productSku?: IProductSku;
}

export interface IDeviceItem extends ISharedProductItem {
    useRetailPrice: boolean;
    useAssurance: boolean;
    assuranceSku?: ISku;
    usePricingOptions: boolean;
    tradeIn?: ITradeIn;
    downPayment: number;
    monthlyPrice: number;
    memoryOption: string;
    colorName: string;
}

export const DefaultDeviceItem = (): IDeviceItem => {
    return {
        product: DefaultProduct(),
        image: '',
        useRetailPrice: false,
        useAssurance: false,
        usePricingOptions: true,
        downPayment: 1,
        monthlyPrice: 1,
        memoryOption: 'testMemory',
        colorName: 'testColor'
    };
};

export interface ICustomerInfo {
    firstName: string;
    lastName: string;
    address: IAddress;
    billingAddress?: IBillingAddress;
    isDifferentBillingAddress: boolean;
    driversLicenseNumber: string;
    driversLicenseState: string;
    phoneNumber: string;
    emailAddress: string;
    dob: string;
    customerId: number;
    employer: string;
}

export interface IAlternateContact {
    firstName: string;
    lastName: string;
    address: IAddress;
    phoneNumber: string;
}

export const DefaultAlternateContact = (): IAlternateContact => {
    return {
        address: DefaultAddress(),
        firstName: '',
        lastName: '',
        phoneNumber: ''
    };
};

export interface IBillingAddress {
    firstName: string;
    lastName: string;
    address: IAddress;
    dob: string;
}

export interface ICustomerAgreements {
    acceptedServiceContract: boolean;
    acceptedMonthlyPaymentContract: boolean;
    contractUrls: IContractUrl[];
    creditCheckInformation: ICreditCheckInformation;
}

export interface ICreditCheckInformation {
    creditCheckPhoneNumber?: string;
    creditChecked: boolean;
    passedCreditCheck: boolean;
    addedPhoneNumber: boolean;
}

export const DefaultCustomerInfo = (): ICustomerInfo => {
    return {
        driversLicenseNumber: '',
        driversLicenseState: '',
        firstName: 'First',
        lastName: 'Last',
        address: {
            addressLine1: '123 Sesame Street',
            addressLine2: '',
            city: 'New York City',
            state: 'New York',
            zipcode: '1234'
        },
        emailAddress: 'iq@form.com',
        phoneNumber: '1234567890',
        dob: new Date().toDateString(),
        customerId: 0,
        isDifferentBillingAddress: false,
        employer: ''
    };
};

export interface ICustomerAgreements {
    acceptedServiceContract: boolean;
}

export interface IShippingMethod {
    cost: number;
    method: ShippingMethod;
    address: IAddress;
    overnight: boolean;
}

export interface IAddress {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zipcode: string;
    timeZone?: string;
}

export const DefaultAddress = (): IAddress => {
    return {
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zipcode: ''
    };
};

export const DefaultCartContext = (): ICartContext => {
    const id = uuidv4();

    return {
        cartStepIndex: 0,
        isCartLoaded: false,
        cart: {
            paymentConfirmationId: id,
            customerInfo: {
                driversLicenseNumber: '',
                driversLicenseState: '',
                firstName: '',
                lastName: '',
                address: DefaultAddress(),
                phoneNumber: '',
                emailAddress: '',
                dob: '',
                customerId: 0,
                isDifferentBillingAddress: false,
                employer: ''
            },
            alternateContact: {
                firstName: '',
                lastName: '',
                address: DefaultAddress(),
                phoneNumber: ''
            },
            isDuplicateCustomer: false,
            autopay: true,
            eBill: true,
            agreements: {
                acceptedServiceContract: false,
                acceptedMonthlyPaymentContract: false,
                contractUrls: [],
                creditCheckInformation: {
                    creditChecked: false,
                    passedCreditCheck: false,
                    addedPhoneNumber: false
                }
            },
            lineItems: [],
            accessories: [],
            shippingMethod: {
                cost: DefaultShippingCost().store,
                method: ShippingMethod.StorePickup,
                address: DefaultAddress(),
                overnight: false
            },
            monthlySummary: [],
            dueTodaySummary: [],
            fees: [],
            reserveNumberResponse: {
                reservationId: '12345',
                reservationItems: []
            }
        },
        setCart: () => {},
        setCartStepIndex: () => {},
        reloadSession: () => {}
    };
};

export interface IContractUrl {
    lineNumber: number;
    url: string;
}
